import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useSubscription } from '../../hooks/useSubscription';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiCheck, FiCreditCard, FiShield, FiUsers, FiBarChart3, FiClock } = FiIcons;

const SubscriptionPage = () => {
  const { processPayment, userSubscription, loading } = useSubscription();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);

  const features = [
    {
      icon: FiCheck,
      title: 'Unlimited Medicines',
      description: 'Add and track unlimited medicines with custom schedules'
    },
    {
      icon: FiUsers,
      title: 'Family Sharing',
      description: 'Share medicine schedules with family members and caregivers'
    },
    {
      icon: FiBarChart3,
      title: 'Advanced Analytics',
      description: 'Detailed reports and insights on medicine adherence'
    },
    {
      icon: FiShield,
      title: 'Priority Support',
      description: '24/7 customer support and assistance'
    },
    {
      icon: FiClock,
      title: 'Smart Reminders',
      description: 'Email and push notifications for medicine times'
    }
  ];

  const handleUpgrade = async () => {
    setProcessing(true);
    try {
      // In a real implementation, you would integrate with a payment gateway
      // For demo purposes, we'll simulate the payment process
      
      const paymentData = {
        amount: 100,
        currency: 'AED',
        user_id: user.id,
        user_email: user.email,
        user_name: user.name || user.email?.split('@')[0]
      };

      // Simulate payment processing delay
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const result = await processPayment(paymentData);
      
      if (result.success) {
        toast.success('Payment successful! Welcome to premium MedTracker!');
        navigate('/dashboard');
      } else {
        throw new Error(result.error || 'Payment failed');
      }
    } catch (error) {
      toast.error(error.message || 'Payment failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  if (userSubscription?.status === 'active') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-md w-full"
        >
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <SafeIcon icon={FiCheck} className="text-3xl text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">You're All Set!</h1>
          <p className="text-gray-600 mb-6">
            You already have lifetime access to MedTracker premium features.
          </p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/dashboard')}
            className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
          >
            Go to Dashboard
          </motion.button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                <SafeIcon icon={FiIcons.FiActivity} className="text-white text-xl" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">MedTracker</h1>
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              className="text-gray-600 hover:text-gray-900 font-medium"
            >
              ← Back to App
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            Upgrade to <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">Premium</span>
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            Get lifetime access to all premium features for just AED 100
          </p>
          
          {userSubscription?.status === 'trial' && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-8 inline-block">
              <div className="flex items-center space-x-2 text-yellow-800">
                <SafeIcon icon={FiClock} />
                <span className="font-medium">
                  {userSubscription.days_left} days left in your trial
                </span>
              </div>
            </div>
          )}
        </motion.div>

        {/* Pricing Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl shadow-xl border-2 border-purple-200 p-8 mb-12 relative overflow-hidden"
        >
          {/* Popular Badge */}
          <div className="absolute top-0 right-0 bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-6 py-2 rounded-bl-2xl font-semibold">
            Best Value
          </div>

          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Lifetime Premium</h2>
            <div className="flex items-center justify-center space-x-2 mb-4">
              <span className="text-5xl font-bold text-purple-600">AED 100</span>
              <div className="text-left">
                <div className="text-gray-500 line-through">AED 200</div>
                <div className="text-green-600 font-medium">50% OFF</div>
              </div>
            </div>
            <p className="text-gray-600">One-time payment • Lifetime access • No recurring fees</p>
          </div>

          {/* Features */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 + index * 0.1 }}
                className="flex items-start space-x-3"
              >
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <SafeIcon icon={feature.icon} className="text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">{feature.title}</h3>
                  <p className="text-sm text-gray-600">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Payment Button */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleUpgrade}
            disabled={processing || loading}
            className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-4 px-8 rounded-xl font-bold text-lg hover:from-purple-700 hover:to-indigo-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-3"
          >
            <SafeIcon icon={FiCreditCard} className="text-xl" />
            <span>
              {processing ? 'Processing Payment...' : 'Upgrade Now - AED 100'}
            </span>
          </motion.button>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500">
              🔒 Secure payment • 💯 30-day money-back guarantee • 📧 Instant access
            </p>
          </div>
        </motion.div>

        {/* Testimonials */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          {[
            {
              name: "Sarah Ahmed",
              role: "Diabetes Patient",
              text: "MedTracker has transformed how I manage my medications. The family sharing feature gives me peace of mind."
            },
            {
              name: "Dr. Mohamed Ali",
              role: "Family Physician", 
              text: "I recommend MedTracker to all my patients. The adherence tracking helps improve treatment outcomes."
            },
            {
              name: "Fatima Hassan",
              role: "Caregiver",
              text: "Managing my mother's medications is so much easier now. The reminders ensure she never misses a dose."
            }
          ].map((testimonial, index) => (
            <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <p className="text-gray-600 mb-4 italic">"{testimonial.text}"</p>
              <div>
                <div className="font-semibold text-gray-900">{testimonial.name}</div>
                <div className="text-sm text-gray-500">{testimonial.role}</div>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  );
};

export default SubscriptionPage;